import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MapPin, Loader2, Plus, Info, Search, AlertTriangle, Star, LogIn } from "lucide-react";
import posthog from "posthog-js";
import { Mascot } from "@/components/Mascot";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useBadgeCheck } from "@/hooks/useBadges";
import { useGooglePlaces, PlacePrediction } from "@/hooks/useGooglePlaces";
import { useDebounce } from "@/hooks/use-debounce";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { QuickAmenitiesSection, QuickAmenities } from "@/components/submit-cafe/QuickAmenitiesSection";

/** Analytics never blocks the flow. */
function track(event: string, properties?: Record<string, unknown>) {
  try {
    posthog.capture(event, properties);
  } catch {
    /* ignore */
  }
}

interface SubmitCafeDialogProps {
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Where the dialog was opened from — surfaces in analytics. */
  entryPoint?: string;
  /** Pre-fills the Places search box (still requires picking a prediction). */
  initialQuery?: string;
}

export function SubmitCafeDialog({
  trigger,
  onSuccess,
  open: controlledOpen,
  onOpenChange,
  entryPoint = "unknown",
  initialQuery,
}: SubmitCafeDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, isLoading: isAuthLoading } = useAuth();
  const { checkBadges } = useBadgeCheck();


  // Form fields
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [state, setState] = useState("");
  const [notes, setNotes] = useState("");
  const [submitterRating, setSubmitterRating] = useState<number | null>(null);
  const [quickAmenities, setQuickAmenities] = useState<QuickAmenities>({});
  
  // Duplicate detection state
  const [existingCafe, setExistingCafe] = useState<{ slug: string; name: string } | null>(null);
  const [existingSubmission, setExistingSubmission] = useState<{ name: string } | null>(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);

  // Google Places search state
  const [searchQuery, setSearchQuery] = useState("");
  const [showPredictions, setShowPredictions] = useState(false);
  const [hasSelectedPlace, setHasSelectedPlace] = useState(false);
  const [dropDirection, setDropDirection] = useState<"down" | "up">("down");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounce(searchQuery, 300);

  const {
    predictions,
    isSearching,
    isFetchingDetails,
    searchPlaces,
    getPlaceDetails,
    clearPredictions,
    reset: resetGooglePlaces,
  } = useGooglePlaces();

  // Trigger search when user types
  useEffect(() => {
    if (hasSelectedPlace) return;
    if (debouncedQuery.length >= 3) {
      searchPlaces(debouncedQuery);
      setShowPredictions(true);
    } else {
      clearPredictions();
      setShowPredictions(false);
    }
  }, [debouncedQuery, hasSelectedPlace, searchPlaces, clearPredictions]);

  // Decide whether to drop predictions up or down based on available space
  useEffect(() => {
    if (!showPredictions || predictions.length === 0) return;
    const el = searchInputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const desired = 240; // matches max-h-60
    if (spaceBelow < desired && spaceAbove > spaceBelow) {
      setDropDirection("up");
    } else {
      setDropDirection("down");
    }
  }, [showPredictions, predictions.length]);

  const isSignedOut = !isAuthLoading && !user;
  const submittedRef = useRef(false);
  const openTrackedRef = useRef(false);

  // Fire open-time analytics and seed the pre-filled query once per open.
  useEffect(() => {
    if (!open) {
      openTrackedRef.current = false;
      return;
    }
    if (isAuthLoading || openTrackedRef.current) return;
    openTrackedRef.current = true;
    submittedRef.current = false;
    if (!user) {
      track("cafe_submission_signin_prompted", { entry_point: entryPoint });
      return;
    }
    track("cafe_submission_opened", {
      entry_point: entryPoint,
      prefilled_query: Boolean(initialQuery?.trim()),
    });
    if (initialQuery?.trim()) {
      setSearchQuery(initialQuery.trim());
    }
  }, [open, isAuthLoading, user, entryPoint, initialQuery]);

  const handleOpenChange = (next: boolean) => {
    if (!next && open) {
      // Only count abandonment when the form was actually shown.
      if (!submittedRef.current && !isSignedOut) {
        track("cafe_submission_abandoned", {
          entry_point: entryPoint,
          had_place_selected: hasSelectedPlace,
        });
      }
    }
    setOpen(next);
  };


  const resetForm = () => {
    setName("");
    setAddress("");
    setCity("");
    setNeighborhood("");
    setState("");
    setNotes("");
    setSubmitterRating(null);
    setQuickAmenities({});
    setSearchQuery("");
    setShowPredictions(false);
    setHasSelectedPlace(false);
    setExistingCafe(null);
    setExistingSubmission(null);
    setSelectedPlaceId(null);
    resetGooglePlaces();
  };

  const handleSelectPlace = async (prediction: PlacePrediction) => {
    setHasSelectedPlace(true);
    setShowPredictions(false);
    clearPredictions();
    setSearchQuery(prediction.structured_formatting.main_text);
    setExistingCafe(null);
    setExistingSubmission(null);
    setSelectedPlaceId(prediction.place_id);

    // Check if this place already exists in the cafes directory
    const [cafeCheck, submissionCheck] = await Promise.all([
      supabase
        .from("cafes")
        .select("slug, name")
        .eq("google_place_id", prediction.place_id)
        .eq("is_active", true)
        .limit(1)
        .maybeSingle(),
      supabase
        .from("cafe_submissions")
        .select("id, name")
        .eq("google_place_id", prediction.place_id)
        .eq("status", "pending")
        .limit(1)
        .maybeSingle(),
    ]);

    track("cafe_submission_place_selected", {
      entry_point: entryPoint,
      has_place_id: Boolean(prediction.place_id),
    });

    if (cafeCheck.data) {
      setExistingCafe(cafeCheck.data);
      track("cafe_submission_duplicate_blocked", { entry_point: entryPoint, kind: "existing_cafe" });
    }
    if (submissionCheck.data) {
      setExistingSubmission(submissionCheck.data);
      track("cafe_submission_duplicate_blocked", { entry_point: entryPoint, kind: "pending_submission" });
    }

    const details = await getPlaceDetails(prediction.place_id);
    if (details) {
      const getComponent = (type: string) =>
        details.address_components.find((c) => c.types.includes(type))?.long_name || "";

      setName(details.name);
      const streetNumber = getComponent("street_number");
      const route = getComponent("route");
      setAddress(`${streetNumber} ${route}`.trim());
      const cityValue = getComponent("locality") || getComponent("postal_town");
      setCity(cityValue);
      const neighborhoodValue =
        getComponent("neighborhood") ||
        getComponent("sublocality") ||
        getComponent("sublocality_level_1") ||
        "";
      setNeighborhood(neighborhoodValue);
      setState(getComponent("administrative_area_level_1"));
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (hasSelectedPlace && value !== name) {
      setHasSelectedPlace(false);
      setExistingCafe(null);
      setExistingSubmission(null);
      setSelectedPlaceId(null);
      setName("");
      setAddress("");
      setCity("");
      setNeighborhood("");
      setState("");
    }
  };

  // Build a clean details payload, omitting empty values
  const buildDetailsPayload = (): Record<string, unknown> | null => {
    const d: Record<string, unknown> = {};
    if (Object.keys(quickAmenities).length > 0) {
      d.quick_amenities = quickAmenities;
    }
    return Object.keys(d).length > 0 ? d : null;
  };

  const handleSubmit = async () => {
    if (!name.trim() || !address.trim() || !city.trim()) {
      toast({
        title: "Missing information",
        description: "Please fill in the cafe name, address, and city",
        variant: "destructive",
      });
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to submit a cafe",
        variant: "destructive",
        action: <ToastAction altText="Sign In" onClick={() => navigate("/auth", { state: { from: window.location.pathname } })}>Sign In</ToastAction>,
      });
      return;
    }

    setIsSubmitting(true);

    // Check for duplicates by google_place_id
    if (selectedPlaceId) {
      const { data: existing } = await supabase
        .from("cafe_submissions")
        .select("id, name")
        .eq("google_place_id", selectedPlaceId)
        .limit(1);

      if (existing && existing.length > 0) {
        setIsSubmitting(false);
        // Duplicate is its own outcome — do NOT also fire submit_failed.
        track("cafe_submission_duplicate_blocked", { entry_point: entryPoint, kind: "pending_submission" });
        toast({
          title: "Already submitted",
          description: `This cafe was already submitted. Check back soon!`,
          variant: "destructive",
        });
        return;
      }
    }

    const detailsPayload = buildDetailsPayload();

    const { error } = await supabase.from("cafe_submissions").insert({
      name: name.trim(),
      address: address.trim(),
      city: city.trim(),
      neighborhood: neighborhood.trim() || null,
      state: state.trim() || null,
      notes: notes.trim() || null,
      submitted_by: session.user.id,
      status: "pending" as const,
      google_place_id: selectedPlaceId,
      submitter_rating: submitterRating,
      ...(detailsPayload ? { details: detailsPayload as unknown as Record<string, unknown> } : {}),
    } as any);

    setIsSubmitting(false);

    if (error) {
      console.error("Submission error:", error);
      track("cafe_submission_submit_failed", { entry_point: entryPoint, reason: error.message });
      toast({
        title: "Submission failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    submittedRef.current = true;
    track("cafe_submission_submitted", {
      entry_point: entryPoint,
      has_rating: submitterRating != null,
      has_notes: Boolean(notes.trim()),
      amenity_count: Object.keys(quickAmenities).length,
    });

    toast({
      title: "Cafe submitted!",
      description: "Thanks for your suggestion. We'll review it soon!",
    });

    checkBadges(session.user.id, 'submission');

    setOpen(false);
    resetForm();
    onSuccess?.();
  };

  const isControlled = controlledOpen !== undefined;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" size="sm" className="gap-2">
              <Plus className="h-4 w-4" />
              Suggest a Cafe
            </Button>
          )}
        </DialogTrigger>
      )}
      {isSignedOut ? (
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <Mascot mood="happy" size="md" className="shrink-0" />
              <div className="space-y-1 pt-1">
                <DialogTitle className="font-display">Sign in to suggest a café</DialogTitle>
                <DialogDescription>
                  We check every café by hand before it goes in, so we need to be able to tell you when yours is live.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="pt-2">
            <Button
              className="w-full gap-2"
              onClick={() => {
                setOpen(false);
                navigate("/auth", { state: { from: window.location.pathname } });
              }}
            >
              <LogIn className="h-4 w-4" />
              Sign in
            </Button>
          </div>
        </DialogContent>
      ) : (
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <Mascot mood="happy" size="md" className="shrink-0" />
            <div className="space-y-1 pt-1">
              <DialogTitle className="font-display">Suggest a Cafe</DialogTitle>
              <DialogDescription>
                Know a great work-friendly cafe? Search for it below!
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>


        <div className="space-y-4 py-4">
          {/* Google Places Search Input */}
          <div className="space-y-2">
            <Label htmlFor="cafe-search">Search for a Cafe *</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="cafe-search"
                ref={searchInputRef}
                value={searchQuery}
                onChange={handleSearchInputChange}
                placeholder="Start typing a cafe name..."
                className="pl-9 pr-9 focus-visible:ring-honey/50 focus-visible:border-honey"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                data-form-type="other"
                inputMode="search"
              />
              {(isSearching || isFetchingDetails) && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
              )}

              {/* Predictions dropdown */}
              {showPredictions && predictions.length > 0 && (
                <div
                  className={`absolute z-[60] w-full bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto ${
                    dropDirection === "up" ? "bottom-full mb-1" : "top-full mt-1"
                  }`}
                >
                  {predictions.map((prediction) => (
                    <button
                      key={prediction.place_id}
                      type="button"
                      onClick={() => handleSelectPlace(prediction)}
                      className="w-full px-3 py-2 text-left hover:bg-secondary/50 transition-colors border-b border-border last:border-b-0"
                    >
                      <div className="font-medium text-sm">{prediction.structured_formatting.main_text}</div>
                      <div className="text-xs text-muted-foreground">
                        {prediction.structured_formatting.secondary_text}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          {hasSelectedPlace && name && (
            <>
              <p className="text-xs text-muted-foreground">
                Selected: <span className="font-medium text-foreground">{name}</span>
              </p>
              {existingCafe && (
                <Alert className="bg-destructive/10 border-destructive/30">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  <AlertDescription className="text-foreground/80">
                    This cafe is already in our directory!{" "}
                    <Link
                      to={`/cafe/${existingCafe.slug}`}
                      className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                      onClick={() => handleOpenChange(false)}
                    >
                      View {existingCafe.name}
                    </Link>
                  </AlertDescription>
                </Alert>
              )}
              {existingSubmission && !existingCafe && (
                <Alert className="bg-amber-500/10 border-amber-500/30">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-foreground/80">
                    This cafe has already been submitted and is pending review. Check back soon!
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
          </div>

          {/* Info banner — shown after place selection */}
          {hasSelectedPlace && !existingCafe && !existingSubmission && (
            <div className="rounded-lg bg-[#f5ecd7]/50 border border-[#f5ecd7] px-3.5 py-2.5 flex items-start gap-2.5">
              <Info className="h-4 w-4 text-foreground/50 mt-0.5 shrink-0" />
              <p className="text-xs text-foreground/60 leading-relaxed">
                Only share what you know — all fields below are optional. Our team verifies every submission before it goes live.
              </p>
            </div>
          )}

          {/* Auto-filled address fields (shown after selection) */}
          {hasSelectedPlace && (
            <>
              <div className="space-y-2">
                <Label htmlFor="cafe-address">Street Address</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="cafe-address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="123 Main Street"
                    className="pl-9 focus-visible:ring-honey/50 focus-visible:border-honey bg-muted/30"
                    maxLength={200}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cafe-city">City</Label>
                  <Input
                    id="cafe-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="San Francisco"
                    maxLength={100}
                    className="focus-visible:ring-honey/50 focus-visible:border-honey bg-muted/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cafe-state">State</Label>
                  <Input
                    id="cafe-state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="CA"
                    maxLength={50}
                    className="focus-visible:ring-honey/50 focus-visible:border-honey bg-muted/30"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cafe-neighborhood">Neighborhood (optional)</Label>
                <Input
                  id="cafe-neighborhood"
                  value={neighborhood}
                  onChange={(e) => setNeighborhood(e.target.value)}
                  placeholder="e.g. Mission, Capitol Hill"
                  maxLength={100}
                  className="focus-visible:ring-honey/50 focus-visible:border-honey bg-muted/30"
                />
              </div>

              {/* Quick amenities — optional chip selectors */}
              <QuickAmenitiesSection amenities={quickAmenities} onChange={setQuickAmenities} />
            </>
          )}

          {hasSelectedPlace && (
            <div className="space-y-2">
              <div className="space-y-0.5">
                <Label>Write the first review</Label>
                <p className="text-xs text-muted-foreground">
                  If your suggestion is approved, this will be published as your review.
                </p>
              </div>
              <div className="flex items-center gap-1.5 pt-1">
                {[1, 2, 3, 4, 5].map((n) => {
                  const filled = submitterRating !== null && n <= submitterRating;
                  return (
                    <button
                      key={n}
                      type="button"
                      aria-label={`${n} star${n > 1 ? "s" : ""}`}
                      onClick={() =>
                        setSubmitterRating((prev) => (prev === n ? null : n))
                      }
                      className="p-1 -m-1 transition-transform hover:scale-110 active:scale-95"
                    >
                      <Star
                        className={
                          filled
                            ? "h-7 w-7 fill-caramel text-caramel transition-colors"
                            : "h-7 w-7 text-muted-foreground/40 transition-colors"
                        }
                      />
                    </button>
                  );
                })}
                {submitterRating !== null && (
                  <button
                    type="button"
                    onClick={() => setSubmitterRating(null)}
                    className="ml-2 text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {hasSelectedPlace && (
            <div className="space-y-2">
              <Label htmlFor="cafe-notes">Your review</Label>
              <Textarea
                id="cafe-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="What makes this cafe great for work? Wifi, seating, vibe…"
                rows={3}
                maxLength={500}
                className="focus-visible:ring-honey/50 focus-visible:border-honey"
              />
              <div className="flex items-center justify-between gap-2">
                {notes.trim().length > 0 && submitterRating === null ? (
                  <p className="text-xs text-caramel">
                    Add a star rating to publish this as your review.
                  </p>
                ) : (
                  <span />
                )}
                <p className="text-xs text-muted-foreground text-right">{notes.length}/500</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            className="flex-1" 
            onClick={handleSubmit} 
            disabled={isSubmitting || !hasSelectedPlace || !!existingCafe || !!existingSubmission}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Cafe"
            )}
          </Button>
        </div>
      </DialogContent>
      )}
    </Dialog>
  );
}
